/**
 * Provider-neutral auth surface: what `useAuthSession()` returns whichever
 * provider (`workos` or `oidc`) the build selected.
 */

/** ID-token profile. The WorkOS profile mapping and an OIDC `IdTokenClaims` both satisfy this. */
export interface AuthUser {
  sub?: string;
  name?: string;
  nickname?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  picture?: string;
  [claim: string]: unknown;
}

export interface GetTokenOptions {
  /** `off` forces a fresh token from the issuer. */
  cacheMode?: 'on' | 'off' | 'cache-only';
  authorizationParams?: {
    audience?: string;
    scope?: string;
  };
}

export interface LoginOptions {
  /** Round-tripped through the IdP; `returnTo` restores the pre-login route. */
  appState?: { returnTo?: string };
  authorizationParams?: {
    login_hint?: string;
    /** Upstream connection name. OIDC issuers receive it as an extra query parameter; WorkOS drops it. */
    connection?: string;
    prompt?: 'none' | 'login' | 'consent' | 'select_account';
    [param: string]: string | undefined;
  };
}

export interface LogoutOptions {
  /** `false` clears the local session without redirecting to the IdP. */
  openUrl?: false;
  logoutParams?: { returnTo?: string };
}

export interface AuthSession {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: AuthUser;
  error?: Error;
  getAccessTokenSilently(options?: GetTokenOptions): Promise<string>;
  getIdTokenClaims(): Promise<Record<string, unknown> | undefined>;
  loginWithRedirect(options?: LoginOptions): Promise<void>;
  logout(options?: LogoutOptions): Promise<void>;
}

/**
 * Error shape `hooks/useAuth` treats as terminal (dead refresh token, no
 * session): the OAuth `error` / `error_description` fields, so the same check
 * covers both providers.
 */
export function loginRequiredError(description = 'Login required'): Error {
  return Object.assign(new Error(description), {
    error: 'login_required',
    error_description: description,
  });
}
